/**
 * #3016: the tenant's manual order of product categories, products within a
 * category and rooms/objects within a location — as the admin-ui lists them.
 *
 * Placed rows (`sortOrder` set) first, by position; never-placed rows after
 * them by name — the order the backend lists them in. MIRROR of
 * `new-backend/admin/src/lib/catalogue/manualSortOrder.ts` (the server-side
 * rule and the writer); changed together.
 */

/** Placed before unplaced, by position; 0 when neither decides. */
export const compareSortOrder = (
    a: number | null | undefined,
    b: number | null | undefined,
): number => {
    const aPlaced = typeof a === 'number';
    const bPlaced = typeof b === 'number';
    if (aPlaced && bPlaced) return (a as number) - (b as number);
    if (aPlaced) return -1;
    if (bPlaced) return 1;
    return 0;
};

/** A copy of `list` in the manual order, then by `nameOf`. */
export const sortByManualOrder = <T extends { sortOrder?: number | null }>(
    list: readonly T[],
    nameOf: (item: T) => string,
): T[] => {
    const byName = (a: T, b: T) =>
        nameOf(a).localeCompare(nameOf(b), 'de', { sensitivity: 'base', numeric: true });
    return [...list].sort((a, b) => compareSortOrder(a.sortOrder, b.sortOrder) || byName(a, b));
};

/**
 * `ids` with the entry at `from` moved to `to` — the one operation behind
 * drag and drop and the up/down buttons. Out-of-range indices change nothing.
 */
export const moveId = (ids: readonly string[], from: number, to: number): string[] => {
    if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) return [...ids];
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};
